// Push/pull for the `check_ins` table. Push translates Dexie rows (with a
// JS `Date` in `date`) into the server schema (with a `yyyy-MM-dd` string in
// `entry_date`). Pull does the inverse and applies last-write-wins by
// `updated_at` so a stale incoming row never clobbers a fresher local edit.

import { format } from 'date-fns';
import { z } from 'zod';
import { db, withDeleteHookSuppressed, type CheckIn } from '../db';
import { isSupabaseConfigured, supabase } from '../supabase/client';
import { cursorOrEpoch, readCursor, writeCursor } from './cursor';
import { dedupeCheckInByContent } from './dedupe';
import type { SyncResult } from './types';
import { EMPTY_SYNC_RESULT } from './types';

// Runtime contract for what the server hands us. Replacing the prior
// `data as RemoteCheckInRow[]` cast so a column rename / type drift on the
// server surfaces as a parse error in result.errors rather than silently
// crashing inside applyRemoteCheckIn. `id` is the server PK (uuid),
// `client_id` is our owner-scoped natural key; both are uuids in the schema
// but accepted as plain strings here because supabase-js doesn't guarantee
// canonical formatting and we only use them for equality.
const remoteCheckInRowSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  owner_id: z.string(),
  type: z.enum(['morning', 'noon', 'evening']),
  entry_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  defects: z.array(z.string()).nullable(),
  assets: z.array(z.string()).nullable(),
  rating: z.number().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});

type RemoteCheckInRow = z.infer<typeof remoteCheckInRowSchema>;

interface PushPayload {
  client_id: string;
  owner_id: string;
  type: CheckIn['type'];
  entry_date: string;
  defects: string[];
  assets: string[];
  rating: number;
  notes: string;
  updated_at: string;
  deleted_at: string | null;
}

export function buildCheckInPushPayload(
  row: CheckIn,
  ownerId: string,
  now: Date = new Date(),
): PushPayload {
  return {
    client_id: row.clientId ?? crypto.randomUUID(),
    owner_id: ownerId,
    type: row.type,
    entry_date: format(row.date, 'yyyy-MM-dd'),
    defects: row.defects,
    assets: row.assets,
    rating: row.rating,
    notes: row.notes,
    updated_at: (row.updatedAt ?? now).toISOString(),
    deleted_at: row.deletedAt ? row.deletedAt.toISOString() : null,
  };
}

// Convert a server row into the Dexie shape. `entry_date` becomes a noon-
// local Date so it lands on the intended calendar day regardless of TZ.
export function applyRemoteCheckIn(
  remote: RemoteCheckInRow,
): Omit<CheckIn, 'id'> {
  const [y, m, d] = remote.entry_date.split('-').map(Number);
  // Construct as local-noon so a DST/TZ shift can't bump the day boundary.
  const localDate =
    Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
      ? new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12, 0, 0)
      : new Date(remote.entry_date);
  return {
    clientId: remote.client_id,
    type: remote.type,
    date: localDate,
    defects: remote.defects ?? [],
    assets: remote.assets ?? [],
    rating: remote.rating ?? 0,
    notes: remote.notes ?? '',
    updatedAt: new Date(remote.updated_at),
    deletedAt: remote.deleted_at ? new Date(remote.deleted_at) : null,
  };
}

export async function syncCheckInsUp(userId: string): Promise<SyncResult> {
  if (!isSupabaseConfigured() || !supabase) return { ...EMPTY_SYNC_RESULT };
  const client = supabase;
  const result: SyncResult = { ...EMPTY_SYNC_RESULT, errors: [] };

  // Push live rows.
  const local = await db.checkIns.toArray();
  const withClientIds = local.filter((r) => r.clientId);
  if (withClientIds.length > 0) {
    const payload = withClientIds.map((r) =>
      buildCheckInPushPayload(r, userId),
    );
    const { error } = await client
      .from('check_ins')
      .upsert(payload, { onConflict: 'owner_id,client_id' });
    if (error) {
      result.errors.push(`check_ins push: ${error.message}`);
    } else {
      result.pushed += payload.length;
    }
  }

  // Push tombstones.
  const tombstones = await db.tombstones
    .where('table')
    .equals('checkIns')
    .toArray();
  if (tombstones.length > 0) {
    const payload = tombstones.map((t) => ({
      client_id: t.clientId,
      owner_id: userId,
      // The server-side row already has the rest of the data; we only need
      // to bump deleted_at. Upsert with these keys + deleted_at is the
      // simplest way to handle "soft-delete by client_id" without a separate
      // RPC. We also supply a `type`/`entry_date` because they're NOT NULL —
      // sentinel values that never round-trip back to a real row.
      type: 'evening' as const,
      entry_date: format(t.deletedAt, 'yyyy-MM-dd'),
      updated_at: t.deletedAt.toISOString(),
      deleted_at: t.deletedAt.toISOString(),
    }));
    const { error } = await client
      .from('check_ins')
      .upsert(payload, { onConflict: 'owner_id,client_id' });
    if (error) {
      result.errors.push(`check_ins tombstone push: ${error.message}`);
    } else {
      result.pushed += payload.length;
      const ids = tombstones
        .map((t) => t.id)
        .filter((id): id is number => id !== undefined);
      if (ids.length > 0) await db.tombstones.bulkDelete(ids);
    }
  }
  return result;
}

export async function syncCheckInsDown(userId: string): Promise<SyncResult> {
  if (!isSupabaseConfigured() || !supabase) return { ...EMPTY_SYNC_RESULT };
  const client = supabase;
  const result: SyncResult = { ...EMPTY_SYNC_RESULT, errors: [] };

  // First sync = cursor unset. Only on the first sync do we run content-
  // based dedupe — that's when a local row created offline on THIS device
  // might collide (by content) with a server row that was pushed by another
  // device. After the first successful pull, every locally-known clientId
  // has either come from the server or been pushed up; subsequent same-
  // content creates are then treated as legitimate distinct entries (the
  // user can delete duplicates from the UI).
  const isFirstSync = readCursor('check_ins') === null;

  const since = cursorOrEpoch('check_ins');
  const { data, error } = await client
    .from('check_ins')
    .select('*')
    .eq('owner_id', userId)
    .gt('updated_at', since)
    .order('updated_at', { ascending: true });
  if (error) {
    result.errors.push(`check_ins pull: ${error.message}`);
    return result;
  }

  // Runtime-validate the shape before we trust it. A column rename or a new
  // null-allowed column on the server should surface here as an actionable
  // error string, not as a downstream `undefined.split()` crash.
  const parsed = z.array(remoteCheckInRowSchema).safeParse(data);
  if (!parsed.success) {
    result.errors.push(
      `check_ins pull: row schema mismatch — ${parsed.error.message}`,
    );
    return result;
  }
  const rows = parsed.data;
  if (rows.length === 0) return result;

  // Pre-compute the set of clientIds in this batch so dedupe can skip
  // local rows whose clientId is also incoming (those are not orphan
  // offline duplicates — they're already mirrored on the server).
  const incomingClientIds: ReadonlySet<string> = new Set(
    rows.map((r) => r.client_id),
  );

  // Pre-fetch pending local tombstones for this table so we don't resurrect
  // a row the user deleted but whose tombstone hasn't been pushed yet. On
  // first-sync ordering (pull-then-push) this is critical: without it, the
  // pull would re-insert a row from the server's pre-delete state, and the
  // subsequent push would tombstone it — leaving the row deleted on the
  // server but with the tombstone-roundtrip echoing back as a delete on
  // the NEXT pull (correct end state, but a wasteful round-trip and a
  // brief window where the deleted row reappears locally).
  const pendingTombstoneClientIds: ReadonlySet<string> = new Set(
    (await db.tombstones.where('table').equals('checkIns').toArray()).map(
      (t) => t.clientId,
    ),
  );

  await withDeleteHookSuppressed(async () => {
    await db.transaction('rw', db.checkIns, async () => {
      for (const remote of rows) {
        // Skip remotes shadowed by a pending local tombstone — the push
        // pass will reconcile by sending the tombstone up.
        if (pendingTombstoneClientIds.has(remote.client_id)) {
          continue;
        }

        // Content-based dedupe runs only on first sync and only for live
        // (non-deleted) remote rows. Running it on a tombstoned remote
        // would cascade-delete a live local row that happens to share a
        // (day, type) — which is wrong when the local was created AFTER
        // the server delete. We intentionally skip the "optional" tombstone-
        // by-content cascade from the spec; users can re-create if needed.
        if (isFirstSync && !remote.deleted_at) {
          await dedupeCheckInByContent(
            remote.client_id,
            remote.entry_date,
            remote.type,
            incomingClientIds,
          );
        }

        const local = await db.checkIns
          .where('clientId')
          .equals(remote.client_id)
          .first();

        if (remote.deleted_at) {
          if (local?.id != null) await db.checkIns.delete(local.id);
          result.pulled += 1;
          continue;
        }

        const incomingUpdated = new Date(remote.updated_at).getTime();
        const localUpdated = local?.updatedAt?.getTime() ?? -Infinity;
        if (local && localUpdated >= incomingUpdated) {
          result.conflicts += 1;
          continue;
        }
        const next = applyRemoteCheckIn(remote);
        if (local?.id != null) {
          await db.checkIns.put({ ...next, id: local.id });
        } else {
          await db.checkIns.add(next);
        }
        result.pulled += 1;
      }
    });
  });

  // Advance cursor to the latest seen `updated_at`. We rely on the server
  // returning in ascending order; the last row's timestamp is our new high
  // water mark.
  const last = rows[rows.length - 1];
  if (last) writeCursor('check_ins', last.updated_at);

  return result;
}
